// CreateUserModal
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from "react-redux";
import { login, signup as _signup, getAllItemsEmptyUser } from '../../actions';
import { sendEmailVerification } from '../../actions/auth.action';
import { MaterialInput } from '../MaterialUi';
import MainButtonSite from './../button/MainButtonSite';
import EmptyButton from './../button/EmptyButton';
import BasicModal from './../MaterialUi/modal';
import Loader from './../Loader';
import ErrorTitle from './../Title/ErrorTitle';
import InputFullBorder from './../Input/InputFullBorder';
import SearchIcon from '@mui/icons-material/Search';
import PasswordError from '../PasswordError';
import RenderSubscriptionsMainPage from '../Checkbox/RenderSubscriptionsMainPage';
import MailCodeModal from './MailCodeModal';
import { setShowModalUser, setShowPasswordResetModal } from '../../reducers/auth.reducer';

const emptyUserForm = {
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    passwordCheck: "",
    contactNumber: "",
    nameOrganization: "",
    taxId: ""
};

const getEmailVerified = (user = {}) => {
    return !!(
        user.emailVerified ||
        user.isEmailVerified ||
        user.emailConfirmed ||
        user.verifiedEmail
    );
};

const CreateUser = ({ compact = false }) => {
    const dispatch = useDispatch();

    const {
        showModalUser,
        loading,
        modalMode,
        modalRole,
        authenticate,
        user: authUser,
        emailVerificationSent
    } = useSelector((state) => state.auth);

    const { mainPages } = useSelector((state) => state.mainPage);

    const isSignup = modalMode === "signup";
    const emailVerified = getEmailVerified(authUser);
    const isEmailVerificationLocked = authenticate && !!authUser?._id && !emailVerified;

    const [subscriptionsArray, setSubscriptionsArray] = useState([]);
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [region, setRegion] = useState("");
    const [typePassword, setTypePassword] = useState(false);
    const [errorShowText, setShowErrorText] = useState(false);
    const [passwordMatch, setPasswordMatch] = useState(false);
    const [textError, setTextError] = useState("");
    const [typeContent, setTypeContent] = useState("");
    const [signupResult, setSignupResult] = useState(null);
    const [verificationDismissed, setVerificationDismissed] = useState(false);

    const clearErrors = () => {
        setShowErrorText(false);
        setTextError("");
    };

    const resetLocalState = () => {
        setShowErrorText(false);
        setTextError("");
        setPasswordMatch(false);
        setTypeContent("");
        setUserForm(emptyUserForm);
        setRegion("");
        setSubscriptionsArray([]);
        setSignupResult(null);
    };

    useEffect(() => {
        if (mainPages?.length === 0) {
            dispatch(getAllItemsEmptyUser("mainPage"));
        }
    }, [mainPages, dispatch]);

    useEffect(() => {
        if (!showModalUser && !isEmailVerificationLocked) {
            resetLocalState();
        }
    }, [showModalUser, isEmailVerificationLocked]);

    useEffect(() => {
        setVerificationDismissed(false);
    }, [authUser?._id, emailVerified]);

    useEffect(() => {
        if (typeof document === "undefined" || !compact || !showModalUser) return undefined;

        document.body.classList.add("scene-guest-auth-compact");

        return () => {
            document.body.classList.remove("scene-guest-auth-compact");
        };
    }, [compact, showModalUser]);

    useEffect(() => {
        if (!isEmailVerificationLocked || verificationDismissed) return;

        setTypeContent("emailVerification");

        setSignupResult((prev) => ({
            ...(prev || {}),
            user: authUser,
            emailVerificationSent: !!(prev?.emailVerificationSent || emailVerificationSent)
        }));

        if (!showModalUser) {
            dispatch(setShowModalUser({
                forceShow: true,
                mode: "signup",
                role: authUser?.role || modalRole || "user"
            }));
        }
    }, [
        isEmailVerificationLocked,
        verificationDismissed,
        showModalUser,
        authUser,
        emailVerificationSent,
        modalRole,
        dispatch
    ]);

    const closeModal = (force = false) => {
        if (isEmailVerificationLocked) {
            setVerificationDismissed(true);
        }

        dispatch(setShowModalUser({ forceShow: false }));
        resetLocalState();
    };

    const toggleMode = () => {
        if (isEmailVerificationLocked) return;

        clearErrors();
        setSignupResult(null);
        setTypeContent("");

        dispatch(setShowModalUser({
            forceShow: true,
            mode: isSignup ? 'login' : 'signup',
            role: modalRole
        }));
    };

    const validateSignupForm = () => {
        if (
            !userForm.firstName ||
            !userForm.lastName ||
            !userForm.email ||
            !userForm.password ||
            !userForm.passwordCheck ||
            !userForm.contactNumber ||
            !region ||
            !passwordMatch
        ) {
            setShowErrorText(true);
            setTextError("Обязательные поля должны быть заполнены");
            return false;
        }

        if ((modalRole === 'cityAdmin' || modalRole === 'partner') && !userForm.nameOrganization) {
            setShowErrorText(true);
            setTextError("Для данной роли укажите название организации");
            return false;
        }

        return true;
    };

    const userSignup = async () => {
        if (!validateSignupForm()) return;

        const user = {
            ...userForm,
            region,
            role: modalRole || 'user',
            subscriptions: subscriptionsArray
        };

        const result = await dispatch(_signup(user));

        if (!result?.ok) {
            setShowErrorText(true);
            setTextError(result?.message || "Ошибка регистрации");
            return;
        }

        let nextResult = result;

        if (!result.emailVerificationSent) {
            const sendResult = await dispatch(sendEmailVerification());

            nextResult = {
                ...result,
                emailVerificationSent: !!sendResult?.ok,
                message: sendResult?.message || result.message
            };
        }

        setSignupResult(nextResult);
        setTypeContent("emailVerification");
        clearErrors();

        dispatch(setShowModalUser({
            forceShow: true,
            mode: "signup",
            role: nextResult?.user?.role || modalRole || "user"
        }));
    };

    const userLogin = async () => {
        if (!userForm.email || !userForm.password) {
            setShowErrorText(true);
            setTextError("Введите email и пароль");
            return;
        }

        const ok = await dispatch(login({
            email: userForm.email,
            password: userForm.password
        }));

        if (!ok) {
            setShowErrorText(true);
            setTextError("Неправильный пароль или логин");
        }
    };

    const handleTypeSubscriptions = () => {
        if (!validateSignupForm()) return;

        clearErrors();
        setTypeContent("subscriptions");
    };

    const openPasswordReset = () => {
        if (isEmailVerificationLocked) return;

        dispatch(setShowModalUser({ forceShow: false }));
        dispatch(setShowPasswordResetModal(true));
    };

    const renderAuthContent = () => (
        <div className="createUserModal__rowContent">
            <div className="createUserModal__loginInputContainer">
                {isSignup && (
                    <>
                        <MaterialInput
                            type="text"
                            label="Имя"
                            value={userForm.firstName}
                            onChange={(e) => setUserForm({ ...userForm, firstName: e.target.value })}
                        />

                        <MaterialInput
                            type="text"
                            label="Фамилия"
                            value={userForm.lastName}
                            onChange={(e) => setUserForm({ ...userForm, lastName: e.target.value })}
                        />

                        <MaterialInput
                            type="text"
                            label="Мобильный"
                            value={userForm.contactNumber}
                            onChange={(e) => setUserForm({ ...userForm, contactNumber: e.target.value })}
                        />

                        {(modalRole === 'cityAdmin' || modalRole === 'partner') && (
                            <>
                                <MaterialInput
                                    type="text"
                                    label="Название организации"
                                    value={userForm.nameOrganization}
                                    onChange={(e) => setUserForm({ ...userForm, nameOrganization: e.target.value })}
                                />

                                <MaterialInput
                                    type="text"
                                    label="УНП / ИНН"
                                    value={userForm.taxId}
                                    onChange={(e) => setUserForm({ ...userForm, taxId: e.target.value })}
                                />
                            </>
                        )}
                    </>
                )}

                <MaterialInput
                    type="email"
                    label="Email"
                    value={userForm.email}
                    onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                />

                <MaterialInput
                    type={typePassword ? "text" : "password"}
                    label="Пароль"
                    value={userForm.password}
                    onClickIcon={() => setTypePassword(!typePassword)}
                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                    iconBlockPassword
                />

                {isSignup && (
                    <>
                        <div className="createUserModal__passwordCheck">
                            <MaterialInput
                                type={typePassword ? "text" : "password"}
                                label="Повторите пароль"
                                value={userForm.passwordCheck}
                                onClickIcon={() => setTypePassword(!typePassword)}
                                onChange={(e) => setUserForm({ ...userForm, passwordCheck: e.target.value })}
                                iconBlockPassword
                            />

                            <PasswordError
                                password={userForm.password}
                                passwordCheck={userForm.passwordCheck}
                                passwordMatch={passwordMatch}
                                setPasswordMatch={setPasswordMatch}
                            />
                        </div>

                        <InputFullBorder
                            typeInput="allCity"
                            icon={<SearchIcon />}
                            label="Ваша область"
                            type="text"
                            value={region}
                            setValue={setRegion}
                        />
                    </>
                )}

                <ErrorTitle text={textError} setShow={setShowErrorText} show={errorShowText} />

                {isSignup ? (
                    <MainButtonSite text="Дальше" onClick={handleTypeSubscriptions} />
                ) : (
                    <MainButtonSite text="Войти" onClick={userLogin} />
                )}

                {!isSignup && (
                    <button
                        type="button"
                        className="createUserModal__forgotButton"
                        onClick={openPasswordReset}
                    >
                        Забыли пароль?
                    </button>
                )}

                <div className="createUserModal__emptyButBox">
                    <EmptyButton
                        onClick={toggleMode}
                        text={isSignup ? "У меня уже есть аккаунт" : "Зарегистрироваться"}
                    />
                </div>
            </div>
        </div>
    );

    const renderSubscriptions = () => (
        <div className="createUserModal__subscriptionsStep">
            <RenderSubscriptionsMainPage
                setTypeContent={setTypeContent}
                items={mainPages}
                setSubscriptionsArray={setSubscriptionsArray}
                subscriptionsArray={subscriptionsArray}
            />

            <div className="createUserModal__stepActions">
                <button
                    type="button"
                    className="createUserModal__backButton"
                    onClick={() => setTypeContent("")}
                >
                    Назад
                </button>

                <MainButtonSite text="Зарегистрироваться" onClick={userSignup} />
            </div>

            <ErrorTitle text={textError} setShow={setShowErrorText} show={errorShowText} />
        </div>
    );

    const renderEmailVerification = () => {
        const verificationEmail = signupResult?.user?.email || authUser?.email || userForm.email;
        const verificationCodeSent = !!(signupResult?.emailVerificationSent || emailVerificationSent);

        return (
            <div className="createUserModal__verificationBox">
                <MailCodeModal
                    embedded
                    showEmbeddedClose
                    visible
                    mode="emailVerification"
                    onClose={() => closeModal()}
                    defaultEmail={verificationEmail}
                    codeAlreadySent={verificationCodeSent}
                    initialMessage={verificationCodeSent
                        ? "Код подтверждения отправлен на email"
                        : "Код не был отправлен автоматически. Отправьте его повторно."}
                    onSuccess={() => {
                        setTimeout(() => {
                            closeModal(true);
                        }, 900);
                    }}
                />

                {!isEmailVerificationLocked && (
                    <button
                        type="button"
                        className="createUserModal__skipButton"
                        onClick={() => closeModal()}
                    >
                        Подтвержу позже
                    </button>
                )}
            </div>
        );
    };

    const getTitle = () => {
        if (typeContent === "subscriptions") return "Рассылка";
        if (typeContent === "emailVerification") return "Подтверждение email";

        return isSignup ? "Регистрация" : "Вход";
    };

    const getDescription = () => {
        if (typeContent === "subscriptions") {
            return "Выберите разделы, по которым хотите получать уведомления. После регистрации мы отправим код подтверждения email.";
        }

        if (typeContent === "emailVerification") {
            return isEmailVerificationLocked
                ? "Подтвердите email, чтобы получить доступ к сайту."
                : "Введите код из письма, чтобы подтвердить email и завершить настройку аккаунта.";
        }

        if (isSignup) {
            return `Создайте аккаунт (${modalRole === 'partner' ? 'Партнёр' : modalRole === 'cityAdmin' ? 'Администрация' : 'Обычный'}).`;
        }

        return "Войдите в аккаунт для продолжения работы с сайтом.";
    };

    if (!showModalUser) return null;

    return (
        <BasicModal
            disableClose={isEmailVerificationLocked}
            hideClose={isEmailVerificationLocked}
        >
            <div className={`createUserModal ${compact ? "createUserModal--compact" : ""}`.trim()}>
                {loading ? (
                    <Loader />
                ) : (
                    <>
                        <div className="createUserModal__header">
                            <h2>{getTitle()}</h2>
                            <p>{getDescription()}</p>
                        </div>

                        {typeContent === "subscriptions" && renderSubscriptions()}
                        {typeContent === "emailVerification" && renderEmailVerification()}
                        {!typeContent && renderAuthContent()}
                    </>
                )}
            </div>
        </BasicModal>
    );
};

export default CreateUser;
